import { useState, useCallback } from "react";
import { useOrganization } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { useSubmitOrder } from "@/hooks/use-orders";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { parseApiError } from "@/lib/api-error";
import { usePcOrderScope } from "@/components/pc/PcOrderScopeProvider";
import type { CheckoutFormData } from "@/components/orders/CheckoutForm";
import type { SelectedProduct } from "@/types/product";

export interface ConfirmedOrder {
  id: string;
  orderNumber?: string;
  /** P/O reference the user entered (or the generated fallback). */
  poNumber?: string;
  /** Eclipse sales-order id returned by the Hajoca submission. */
  hajocaOrderId?: string;
  /** Eclipse order confirmation reference, when returned. */
  orderConfirmation?: string;
  fulfillmentType?: "delivery" | "pickup";
  itemCount: number;
  totalCost?: number;
}

interface UseOrderSubmissionOptions {
  /** Existing order ID for edit mode or autosaved draft. */
  existingOrderId?: string;
  /** Template ID to track usage on submit. */
  selectedTemplateId?: string | null;
  /** Track template usage. */
  onTrackTemplateUsage?: (templateId: string) => void;
  /** Called on unrecoverable failure. */
  onFallbackNavigate?: () => void;
  /** Called after a successful submission (e.g. to invalidate the cart query). */
  onAfterSubmit?: () => void;
}

export function useOrderSubmission({
  existingOrderId,
  selectedTemplateId,
  onTrackTemplateUsage,
  onFallbackNavigate,
  onAfterSubmit,
}: UseOrderSubmissionOptions = {}) {
  const { organization } = useOrganization();
  // PC users act for a contractor: submission uses the contractor's Hajoca
  // customer id, not the PC org's (which has none). Null for contractor users.
  const pcScope = usePcOrderScope();
  const queryClient = useQueryClient();
  const submitOrderMutation = useSubmitOrder();
  const { toast } = useToast();
  const posthog = usePostHog();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState<ConfirmedOrder | null>(null);

  // This flow writes orders via apiRequest directly (not the useCreateOrder/
  // useUpdateOrder mutation hooks), so it must invalidate the orders list query
  // itself. Without this, the cached list (staleTime 5m) omits the just-saved
  // order until a hard refresh.
  const invalidateOrdersList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["/api/orders", organization?.id],
    });
  }, [queryClient, organization?.id]);

  /** Save (create or update) an order. Always returns the order ID. */
  const saveOrder = useCallback(
    async (
      checkoutData: CheckoutFormData,
      selectedProducts: SelectedProduct[],
      options?: { promoteFromCart?: boolean },
    ): Promise<{ orderId: string; orderNumber?: string }> => {
      // Convert SelectedProduct[] to OrderProduct format for API storage
      const orderProducts = selectedProducts.map((p) => ({
        productId: p.uid,
        hajocaProductId: p.hajocaProductId,
        description: p.description,
        quantity: p.quantity,
        unitPrice: p.unitPrice || p.currentPrice || 0,
        totalPrice: (p.unitPrice || p.currentPrice || 0) * p.quantity,
        quantityReceived: 0,
        notes: p.notes || undefined,
      }));

      const orderData: Record<string, unknown> = {
        title: checkoutData.title || `PO-${selectedProducts.length}-items`,
        description: "",
        fulfillmentType: checkoutData.fulfillmentType,
        deliveryDate: "",
        notes: checkoutData.notes || "",
        products: orderProducts,
      };

      // When the user explicitly saves a draft, promote the cart (isCart=true)
      // into a real draft order so it shows up in the orders list.
      if (options?.promoteFromCart) {
        orderData.isCart = false;
      }

      if (selectedTemplateId && selectedTemplateId !== "no-template") {
        onTrackTemplateUsage?.(selectedTemplateId);
      }

      if (existingOrderId) {
        const response = await apiRequest(
          "PATCH",
          `/api/orders/${existingOrderId}`,
          orderData,
        );
        if (!response.ok) {
          throw new Error(`Failed to update order: ${response.status}`);
        }
        const updatedOrder = await response.json();
        return {
          orderId: updatedOrder.uid || existingOrderId,
          orderNumber: updatedOrder.orderNumber,
        };
      } else {
        const response = await apiRequest("POST", "/api/orders", orderData);
        if (!response.ok) {
          throw new Error(`Failed to create order: ${response.status}`);
        }
        const createdOrder = await response.json();
        return {
          orderId: createdOrder.uid,
          orderNumber: createdOrder.orderNumber,
        };
      }
    },
    [existingOrderId, selectedTemplateId, onTrackTemplateUsage],
  );

  /** Place order: save then submit to Hajoca. */
  const placeOrder = useCallback(
    async (
      checkoutData: CheckoutFormData,
      selectedProducts: SelectedProduct[],
    ) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        const result = await saveOrder(checkoutData, selectedProducts);

        const hajocaCustomerId =
          pcScope?.contractor?.hajocaCustomerId ||
          (organization?.publicMetadata?.hajocaCustomerId as string);
        let hajocaOrderId: string | undefined;
        let orderConfirmation: string | undefined;
        if (hajocaCustomerId) {
          const submitResult = await submitOrderMutation.mutateAsync({
            orderId: result.orderId,
            hajocaCustomerId,
          });
          // Submit route response: { success, hajocaOrderId, data: <Hajoca response> }
          hajocaOrderId = submitResult?.hajocaOrderId ?? undefined;
          orderConfirmation =
            submitResult?.data?.data?.orderConfirmation ?? undefined;
        }

        const totalCost = selectedProducts.reduce((sum, p) => {
          const price = p.unitPrice || p.currentPrice || 0;
          return sum + price * p.quantity;
        }, 0);

        setConfirmedOrder({
          id: result.orderId,
          orderNumber: result.orderNumber,
          // Same fallback saveOrder applies when the title is empty
          poNumber: checkoutData.title || `PO-${selectedProducts.length}-items`,
          hajocaOrderId,
          orderConfirmation,
          fulfillmentType: checkoutData.fulfillmentType,
          itemCount: selectedProducts.length,
          totalCost,
        });
        onAfterSubmit?.();
        invalidateOrdersList();
        return result;
      } catch (error: any) {
        console.error("Failed to place order:", error);
        const isSaveError = error?.message?.includes("Failed to update order") ||
          error?.message?.includes("Failed to create order");
        toast({
          title: isSaveError ? "Failed to save order" : "Order submission failed",
          description: isSaveError
            ? "Could not save the order. Please try again."
            : "Your order was saved as a draft. You can submit it from the orders page.",
          variant: "destructive",
        });
        onFallbackNavigate?.();
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, saveOrder, organization, pcScope, submitOrderMutation, toast, onFallbackNavigate, onAfterSubmit, invalidateOrdersList],
  );

  /** Save as draft: save without submitting. */
  const saveDraft = useCallback(
    async (
      checkoutData: CheckoutFormData,
      selectedProducts: SelectedProduct[],
    ) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        const result = await saveOrder(checkoutData, selectedProducts, {
          promoteFromCart: true,
        });

        const totalCost = selectedProducts.reduce((sum, p) => {
          const price = p.unitPrice || p.currentPrice || 0;
          return sum + price * p.quantity;
        }, 0);

        setConfirmedOrder({
          id: result.orderId,
          orderNumber: result.orderNumber,
          fulfillmentType: checkoutData.fulfillmentType,
          itemCount: selectedProducts.length,
          totalCost,
        });
        invalidateOrdersList();
        return result;
      } catch (error) {
        console.error("Failed to save draft:", error);
        posthog?.capture("draft_save_failed", {
          orderId: existingOrderId,
          orgId: organization?.id,
          error: parseApiError(error).message,
        });
        toast({
          title: "Draft not saved",
          description:
            "We couldn't save your draft — your latest changes may not be stored.",
          variant: "destructive",
        });
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      saveOrder,
      invalidateOrdersList,
      posthog,
      existingOrderId,
      organization?.id,
      toast,
    ],
  );

  return {
    isSubmitting,
    confirmedOrder,
    setConfirmedOrder,
    placeOrder,
    saveDraft,
  };
}
